/**
 * Schema registry registrations for all migrated API response types.
 *
 * Each registration maps a schema hash → { verify, unpack } so the
 * flatBufferBaseQuery can automatically deserialize any enveloped response.
 *
 * Phase 5 simplified: handlers return FlatBuffer *T types directly.
 * Components use display helpers (formatUuidT, liftTypeLabel, etc.)
 * for rendering. No field mapping layer.
 */

import * as flatbuffers from "flatbuffers";

// Paginated types
import { PaginatedOrganizations } from "../generated/api/paginated-organizations";
import { PaginatedOrgsLight } from "../generated/api/paginated-orgs-light";
import { PaginatedServers } from "../generated/api/paginated-servers";
import { PaginatedServersLight } from "../generated/api/paginated-servers-light";
import { PaginatedAdapters } from "../generated/api/paginated-adapters";
import { PaginatedAdapterSummaries } from "../generated/api/paginated-adapter-summaries";
import { PaginatedChannels } from "../generated/api/paginated-channels";
import { PaginatedBanks } from "../generated/api/paginated-banks";
import { PaginatedBanksLight } from "../generated/api/paginated-banks-light";
import { PaginatedLifts } from "../generated/api/paginated-lifts";
import { PaginatedUsers } from "../generated/api/paginated-users";
import { PaginatedRoles } from "../generated/api/paginated-roles";
import { PaginatedRolesLight } from "../generated/api/paginated-roles-light";
import { PaginatedKeys } from "../generated/api/paginated-keys";
import { PaginatedKeysLight } from "../generated/api/paginated-keys-light";
import { PaginatedCustomKeys } from "../generated/api/paginated-custom-keys";
import { PaginatedNotifications } from "../generated/api/paginated-notifications";
import { PaginatedDebounceOverrides } from "../generated/api/paginated-debounce-overrides";
import { PaginatedActionBanks } from "../generated/api/paginated-action-banks";
import { PaginatedActionCatalogs } from "../generated/api/paginated-action-catalogs";
import { PaginatedHolidays } from "../generated/api/paginated-holidays";
import { PaginatedGroups } from "../generated/api/paginated-groups";
import { PaginatedLeadingZeroes } from "../generated/api/paginated-leading-zeroes";
import { PaginatedBacnetPoints } from "../generated/api/paginated-bacnet-points";
import { PaginatedMonitorGrids } from "../generated/api/paginated-monitor-grids";
import { PaginatedProtocols } from "../generated/api/paginated-protocols";
import { PaginatedProtocolsLight } from "../generated/api/paginated-protocols-light";
import { PaginatedMessages } from "../generated/api/paginated-messages";
import { PaginatedMessageTypes } from "../generated/api/paginated-message-types";
import { PaginatedMaps } from "../generated/api/paginated-maps";
import { PaginatedAudits } from "../generated/api/paginated-audits";
import { PaginatedChannelModifiers } from "../generated/api/paginated-channel-modifiers";
import { PaginatedTimedActions } from "../generated/api/paginated-timed-actions";
import { PaginatedOffsetTypes } from "../generated/api/paginated-offset-types";
import { PaginatedVendors } from "../generated/api/paginated-vendors";
import { PaginatedExternalEndpoints } from "../generated/api/paginated-external-endpoints";
import { PaginatedDataBlockTypes } from "../generated/api/paginated-data-block-types";
import { TimedAction } from "../generated/archive/timed-action";

// Single-entity types
import { Organization } from "../generated/archive/organization";
import { Server } from "../generated/archive/server";
import { Adapter } from "../generated/archive/adapter";
import { Channel } from "../generated/archive/channel";
import { Bank } from "../generated/archive/bank";
import { Lift } from "../generated/archive/lift";
import { User } from "../generated/archive/user";
import { CreateUserResponse } from "../generated/api/create-user-response";
import { Role } from "../generated/archive/role";
import { Key } from "../generated/archive/key";
import { CustomKey } from "../generated/archive/custom-key";
import { Notification } from "../generated/archive/notification";
import { DebounceOverride } from "../generated/archive/debounce-override";
import { ActionCatalog } from "../generated/archive/action-catalog";
import { ActionBank } from "../generated/archive/action-bank";
import { Holiday } from "../generated/archive/holiday";
import { LeadingZeroes } from "../generated/archive/leading-zeroes";
import { BacnetPoint } from "../generated/archive/bacnet-point";
import { MonitorGrid } from "../generated/archive/monitor-grid";
import { ChannelModifier } from "../generated/archive/channel-modifier";
import { Configuration } from "../generated/api/configuration";
import { OffsetType } from "../generated/api/offset-type";
import { DataBlock } from "../generated/api/data-block";
import { DataBlockType } from "../generated/api/data-block-type";
import { Definition } from "../generated/api/definition";
import { Offset } from "../generated/api/offset";
import { Protocol } from "../generated/api/protocol";
import { Vendor } from "../generated/api/vendor";
import { Device } from "../generated/api/device";
import { Icon } from "../generated/api/icon";
import { Map as FbMap } from "../generated/api/map";
import { Waypoint as FbWaypoint } from "../generated/api/waypoint";
import { GroupWaypoint as FbGroupWaypoint } from "../generated/api/group-waypoint";
import { BankWaypoint as FbBankWaypoint } from "../generated/api/bank-waypoint";
import { Message } from "../generated/api/message";
import { MessageType } from "../generated/api/message-type";
import { External } from "../generated/api/external";
import { ExternalEndpoint } from "../generated/api/external-endpoint";
import { SpecialCatalog } from "../generated/api/special-catalog";
import { SpecialDefinition } from "../generated/api/special-definition";
import { Group } from "../generated/api/group";
import { Audit } from "../generated/api/audit";
import { MutationResponse } from "../generated/api/mutation-response";
import { MtlsSecretsResponse } from "../generated/api/mtls-secrets-response";

// Reporting response types
import { FaultLogResponses } from "../generated/reporting/faults/fault-log-responses";
import { TrafficWaitTimeListResponses } from "../generated/reporting/traffic/traffic-wait-time-list-responses";
import { TrafficSummaryFloorResponses } from "../generated/reporting/traffic/traffic-summary-floor-responses";
import { TrafficSummaryDayResponses } from "../generated/reporting/traffic/traffic-summary-day-responses";
import { UsageDayCycleResponses } from "../generated/reporting/usage-report/usage-day-cycle-responses";
import { UsageComparisonResponses } from "../generated/reporting/usage-report/usage-comparison-responses";
import { UsageDoorTimesResponses } from "../generated/reporting/usage-report/usage-door-times-responses";
import { FlattenedServiceLogResponses } from "../generated/reporting/service/flattened-service-log-responses";
import { ServiceLogResponses } from "../generated/reporting/service/service-log-responses";
import { FrequencyByLiftResponses } from "../generated/reporting/frequency/frequency-by-lift-responses";
import { FrequencyByFaultResponses } from "../generated/reporting/frequency/frequency-by-fault-responses";
import { AllBanksBeneficialResponses } from "../generated/reporting/all-banks/all-banks-beneficial-responses";
import { AllBanksOutOfServiceResponses } from "../generated/reporting/all-banks/all-banks-out-of-service-responses";
import { AllBanksPerformanceResponses } from "../generated/reporting/all-banks/all-banks-performance-responses";
import { AllBanksInterruptionResponses } from "../generated/reporting/all-banks/all-banks-interruption-responses";
import { AllBanksEscalatorsUseResponses } from "../generated/reporting/all-banks/all-banks-escalators-use-responses";
import { AllBanksFireServiceResponses } from "../generated/reporting/all-banks/all-banks-fire-service-responses";

// Development-only Simulation API types
import { SimulationAsset } from "../generated/api/simulation-asset";
import { SimulationAssetList } from "../generated/api/simulation-asset-list";
import { SimulationRun } from "../generated/api/simulation-run";
import { SimulationRunList } from "../generated/api/simulation-run-list";
import { SimulationSandboxList } from "../generated/api/simulation-sandbox-list";
import { SimulationScenario } from "../generated/api/simulation-scenario";
import { SimulationScenarioList } from "../generated/api/simulation-scenario-list";
import { SimulationTargetList } from "../generated/api/simulation-target-list";

import { registerSchema, computeSchemaHash, type SchemaHandler } from "./schemaRegistry";

// ---------------------------------------------------------------------------
// Generic unpack helpers — eliminate per-entity field mapping
// ---------------------------------------------------------------------------

interface PaginatedRoot {
  unpack(): { meta: { total: bigint; page: number } | null; data: unknown[] | null };
}

function unpackPaginated(
  payload: Uint8Array,
  getRootFn: (bb: flatbuffers.ByteBuffer) => PaginatedRoot,
): unknown {
  const bb = new flatbuffers.ByteBuffer(payload);
  const unpacked = getRootFn(bb).unpack();
  return {
    status: "success",
    results: unpacked.data?.length ?? 0,
    data: unpacked.data ?? [],
    total: Number(unpacked.meta?.total ?? 0),
    page: unpacked.meta?.page ?? 1,
  };
}

function unpackSingle(
  payload: Uint8Array,
  getRootFn: (bb: flatbuffers.ByteBuffer) => { unpack(): unknown },
): unknown {
  const bb = new flatbuffers.ByteBuffer(payload);
  return { status: "success", data: getRootFn(bb).unpack() };
}

function paginated(
  name: string,
  getRootFn: (bb: flatbuffers.ByteBuffer) => PaginatedRoot,
): SchemaHandler {
  return { name, verify: () => {}, unpack: (p) => unpackPaginated(p, getRootFn) };
}

function single(
  name: string,
  getRootFn: (bb: flatbuffers.ByteBuffer) => { unpack(): unknown },
): SchemaHandler {
  return { name, verify: () => {}, unpack: (p) => unpackSingle(p, getRootFn) };
}

// Report response types use {results: [...]} instead of {meta, data}.
interface ReportRoot {
  unpack(): { results: unknown[] | null };
}

function unpackReport(
  payload: Uint8Array,
  getRootFn: (bb: flatbuffers.ByteBuffer) => ReportRoot,
): unknown {
  const bb = new flatbuffers.ByteBuffer(payload);
  const unpacked = getRootFn(bb).unpack();
  const results = unpacked.results ?? [];
  return { status: "success", results, total: results.length };
}

function report(
  name: string,
  getRootFn: (bb: flatbuffers.ByteBuffer) => ReportRoot,
): SchemaHandler {
  return { name, verify: () => {}, unpack: (p) => unpackReport(p, getRootFn) };
}

// ---------------------------------------------------------------------------
// Registration queue — each entry: [schemaTypeName, handler]
// ---------------------------------------------------------------------------

const registrationQueue: Array<[string, SchemaHandler]> = [
  // Paginated list types
  [
    "Api.PaginatedServersLight",
    paginated("PaginatedServersLight", (bb) =>
      PaginatedServersLight.getRootAsPaginatedServersLight(bb),
    ),
  ],
  [
    "Api.PaginatedOrganizations",
    paginated("PaginatedOrganizations", (bb) =>
      PaginatedOrganizations.getRootAsPaginatedOrganizations(bb),
    ),
  ],
  [
    "Api.PaginatedOrgsLight",
    paginated("PaginatedOrgsLight", (bb) => PaginatedOrgsLight.getRootAsPaginatedOrgsLight(bb)),
  ],
  [
    "Api.PaginatedServers",
    paginated("PaginatedServers", (bb) => PaginatedServers.getRootAsPaginatedServers(bb)),
  ],
  [
    "Api.PaginatedAdapters",
    paginated("PaginatedAdapters", (bb) => PaginatedAdapters.getRootAsPaginatedAdapters(bb)),
  ],
  [
    "Api.PaginatedAdapterSummaries",
    paginated("PaginatedAdapterSummaries", (bb) =>
      PaginatedAdapterSummaries.getRootAsPaginatedAdapterSummaries(bb),
    ),
  ],
  [
    "Api.PaginatedChannels",
    paginated("PaginatedChannels", (bb) => PaginatedChannels.getRootAsPaginatedChannels(bb)),
  ],
  [
    "Api.PaginatedBanks",
    paginated("PaginatedBanks", (bb) => PaginatedBanks.getRootAsPaginatedBanks(bb)),
  ],
  [
    "Api.PaginatedBanksLight",
    paginated("PaginatedBanksLight", (bb) => PaginatedBanksLight.getRootAsPaginatedBanksLight(bb)),
  ],
  [
    "Api.PaginatedLifts",
    paginated("PaginatedLifts", (bb) => PaginatedLifts.getRootAsPaginatedLifts(bb)),
  ],
  [
    "Api.PaginatedUsers",
    paginated("PaginatedUsers", (bb) => PaginatedUsers.getRootAsPaginatedUsers(bb)),
  ],
  [
    "Api.PaginatedRoles",
    paginated("PaginatedRoles", (bb) => PaginatedRoles.getRootAsPaginatedRoles(bb)),
  ],
  [
    "Api.PaginatedRolesLight",
    paginated("PaginatedRolesLight", (bb) => PaginatedRolesLight.getRootAsPaginatedRolesLight(bb)),
  ],
  [
    "Api.PaginatedKeys",
    paginated("PaginatedKeys", (bb) => PaginatedKeys.getRootAsPaginatedKeys(bb)),
  ],
  [
    "Api.PaginatedKeysLight",
    paginated("PaginatedKeysLight", (bb) => PaginatedKeysLight.getRootAsPaginatedKeysLight(bb)),
  ],
  [
    "Api.PaginatedCustomKeys",
    paginated("PaginatedCustomKeys", (bb) => PaginatedCustomKeys.getRootAsPaginatedCustomKeys(bb)),
  ],
  [
    "Api.PaginatedNotifications",
    paginated("PaginatedNotifications", (bb) =>
      PaginatedNotifications.getRootAsPaginatedNotifications(bb),
    ),
  ],
  [
    "Api.PaginatedDebounceOverrides",
    paginated("PaginatedDebounceOverrides", (bb) =>
      PaginatedDebounceOverrides.getRootAsPaginatedDebounceOverrides(bb),
    ),
  ],
  [
    // The by-bank endpoint. Registered so the four verdicts this row carries --
    // action_scope, floor_scope, expected_response and expected_response_timeout
    // -- arrive as typed fields rather than optional JSON keys. Twice now a
    // field the server derived and sent has been dropped on this path and read
    // as `undefined` forever; a decode error is the failure mode we want instead.
    "Api.PaginatedActionBanks",
    paginated("PaginatedActionBanks", (bb) =>
      PaginatedActionBanks.getRootAsPaginatedActionBanks(bb),
    ),
  ],
  [
    "Api.PaginatedActionCatalogs",
    paginated("PaginatedActionCatalogs", (bb) =>
      PaginatedActionCatalogs.getRootAsPaginatedActionCatalogs(bb),
    ),
  ],
  [
    "Api.PaginatedHolidays",
    paginated("PaginatedHolidays", (bb) => PaginatedHolidays.getRootAsPaginatedHolidays(bb)),
  ],
  [
    "Api.PaginatedGroups",
    paginated("PaginatedGroups", (bb) => PaginatedGroups.getRootAsPaginatedGroups(bb)),
  ],
  [
    "Api.PaginatedLeadingZeroes",
    paginated("PaginatedLeadingZeroes", (bb) =>
      PaginatedLeadingZeroes.getRootAsPaginatedLeadingZeroes(bb),
    ),
  ],
  [
    "Api.PaginatedBacnetPoints",
    paginated("PaginatedBacnetPoints", (bb) =>
      PaginatedBacnetPoints.getRootAsPaginatedBacnetPoints(bb),
    ),
  ],
  [
    "Api.PaginatedMonitorGrids",
    paginated("PaginatedMonitorGrids", (bb) =>
      PaginatedMonitorGrids.getRootAsPaginatedMonitorGrids(bb),
    ),
  ],
  // Configuration catalogues (GET /offset-type/, /vendors/, /data-block-type/). Their rows reach
  // the screens through configurationFileAPI, which turns them into the plain shape the screens
  // read (string ids, offset-type attributes as an object): see catalogRows.ts.
  [
    "Api.PaginatedOffsetTypes",
    paginated("PaginatedOffsetTypes", (bb) =>
      PaginatedOffsetTypes.getRootAsPaginatedOffsetTypes(bb),
    ),
  ],
  [
    "Api.PaginatedVendors",
    paginated("PaginatedVendors", (bb) => PaginatedVendors.getRootAsPaginatedVendors(bb)),
  ],
  // GET /external-endpoints/, the catalog the API Clients page grants from
  // (LIFT-1098). LiftCloud writes it as FlatBuffers whatever the Accept says.
  [
    "Api.PaginatedExternalEndpoints",
    paginated("PaginatedExternalEndpoints", (bb) =>
      PaginatedExternalEndpoints.getRootAsPaginatedExternalEndpoints(bb),
    ),
  ],
  [
    "Api.PaginatedDataBlockTypes",
    paginated("PaginatedDataBlockTypes", (bb) =>
      PaginatedDataBlockTypes.getRootAsPaginatedDataBlockTypes(bb),
    ),
  ],
  [
    "Api.PaginatedProtocols",
    paginated("PaginatedProtocols", (bb) => PaginatedProtocols.getRootAsPaginatedProtocols(bb)),
  ],
  [
    "Api.PaginatedProtocolsLight",
    paginated("PaginatedProtocolsLight", (bb) =>
      PaginatedProtocolsLight.getRootAsPaginatedProtocolsLight(bb),
    ),
  ],
  [
    "Api.PaginatedMessages",
    paginated("PaginatedMessages", (bb) => PaginatedMessages.getRootAsPaginatedMessages(bb)),
  ],
  [
    "Api.PaginatedMessageTypes",
    paginated("PaginatedMessageTypes", (bb) =>
      PaginatedMessageTypes.getRootAsPaginatedMessageTypes(bb),
    ),
  ],
  [
    "Api.PaginatedMaps",
    paginated("PaginatedMaps", (bb) => PaginatedMaps.getRootAsPaginatedMaps(bb)),
  ],
  [
    "Api.PaginatedAudits",
    paginated("PaginatedAudits", (bb) => PaginatedAudits.getRootAsPaginatedAudits(bb)),
  ],
  [
    "Api.PaginatedChannelModifiers",
    paginated("PaginatedChannelModifiers", (bb) =>
      PaginatedChannelModifiers.getRootAsPaginatedChannelModifiers(bb),
    ),
  ],
  [
    "Api.PaginatedTimedActions",
    paginated("PaginatedTimedActions", (bb) =>
      PaginatedTimedActions.getRootAsPaginatedTimedActions(bb),
    ),
  ],

  // Single-entity types
  ["Archive.Organization", single("Organization", (bb) => Organization.getRootAsOrganization(bb))],
  ["Archive.Server", single("Server", (bb) => Server.getRootAsServer(bb))],
  ["Archive.Adapter", single("Adapter", (bb) => Adapter.getRootAsAdapter(bb))],
  ["Archive.Channel", single("Channel", (bb) => Channel.getRootAsChannel(bb))],
  ["Archive.Bank", single("Bank", (bb) => Bank.getRootAsBank(bb))],
  ["Archive.Lift", single("Lift", (bb) => Lift.getRootAsLift(bb))],
  ["Archive.User", single("User", (bb) => User.getRootAsUser(bb))],
  // POST /users answers with this rather than a bare User: it is the only
  // response carrying the one-time offline password the server generated, and
  // that password has nowhere to live on Archive.User (which is persisted and
  // exported). Unpacks to { status, data: UserT, userPassword } so callers
  // read `data` exactly as they did before.
  [
    "Api.CreateUserResponse",
    {
      name: "CreateUserResponse",
      verify: () => {},
      unpack: (payload: Uint8Array) => {
        const bb = new flatbuffers.ByteBuffer(payload);
        const unpacked = CreateUserResponse.getRootAsCreateUserResponse(bb).unpack();
        return {
          status: "success",
          data: unpacked.user,
          userPassword: unpacked.userPassword ?? "",
        };
      },
    },
  ],
  ["Archive.Role", single("Role", (bb) => Role.getRootAsRole(bb))],
  ["Archive.Key", single("Key", (bb) => Key.getRootAsKey(bb))],
  ["Archive.CustomKey", single("CustomKey", (bb) => CustomKey.getRootAsCustomKey(bb))],
  ["Archive.Notification", single("Notification", (bb) => Notification.getRootAsNotification(bb))],
  [
    "Archive.DebounceOverride",
    single("DebounceOverride", (bb) => DebounceOverride.getRootAsDebounceOverride(bb)),
  ],
  [
    "Archive.ActionCatalog",
    single("ActionCatalog", (bb) => ActionCatalog.getRootAsActionCatalog(bb)),
  ],
  ["Archive.ActionBank", single("ActionBank", (bb) => ActionBank.getRootAsActionBank(bb))],
  ["Archive.Holiday", single("Holiday", (bb) => Holiday.getRootAsHoliday(bb))],
  [
    "Archive.LeadingZeroes",
    single("LeadingZeroes", (bb) => LeadingZeroes.getRootAsLeadingZeroes(bb)),
  ],
  ["Archive.BacnetPoint", single("BacnetPoint", (bb) => BacnetPoint.getRootAsBacnetPoint(bb))],
  ["Archive.MonitorGrid", single("MonitorGrid", (bb) => MonitorGrid.getRootAsMonitorGrid(bb))],
  [
    "Archive.ChannelModifier",
    single("ChannelModifier", (bb) => ChannelModifier.getRootAsChannelModifier(bb)),
  ],
  ["Archive.TimedAction", single("TimedAction", (bb) => TimedAction.getRootAsTimedAction(bb))],
  ["Api.Configuration", single("Configuration", (bb) => Configuration.getRootAsConfiguration(bb))],
  ["Api.OffsetType", single("OffsetType", (bb) => OffsetType.getRootAsOffsetType(bb))],
  ["Api.DataBlock", single("DataBlock", (bb) => DataBlock.getRootAsDataBlock(bb))],
  ["Api.DataBlockType", single("DataBlockType", (bb) => DataBlockType.getRootAsDataBlockType(bb))],
  ["Api.Definition", single("Definition", (bb) => Definition.getRootAsDefinition(bb))],
  ["Api.Offset", single("Offset", (bb) => Offset.getRootAsOffset(bb))],
  ["Api.Protocol", single("Protocol", (bb) => Protocol.getRootAsProtocol(bb))],
  ["Api.Vendor", single("Vendor", (bb) => Vendor.getRootAsVendor(bb))],
  ["Api.Device", single("Device", (bb) => Device.getRootAsDevice(bb))],
  ["Api.Icon", single("Icon", (bb) => Icon.getRootAsIcon(bb))],
  ["Api.Map", single("Map", (bb) => FbMap.getRootAsMap(bb))],
  ["Api.Waypoint", single("Waypoint", (bb) => FbWaypoint.getRootAsWaypoint(bb))],
  [
    "Api.GroupWaypoint",
    single("GroupWaypoint", (bb) => FbGroupWaypoint.getRootAsGroupWaypoint(bb)),
  ],
  ["Api.BankWaypoint", single("BankWaypoint", (bb) => FbBankWaypoint.getRootAsBankWaypoint(bb))],
  ["Api.Message", single("Message", (bb) => Message.getRootAsMessage(bb))],
  ["Api.MessageType", single("MessageType", (bb) => MessageType.getRootAsMessageType(bb))],
  ["Api.External", single("External", (bb) => External.getRootAsExternal(bb))],
  [
    "Api.ExternalEndpoint",
    single("ExternalEndpoint", (bb) => ExternalEndpoint.getRootAsExternalEndpoint(bb)),
  ],
  [
    "Api.SpecialCatalog",
    single("SpecialCatalog", (bb) => SpecialCatalog.getRootAsSpecialCatalog(bb)),
  ],
  [
    "Api.SpecialDefinition",
    single("SpecialDefinition", (bb) => SpecialDefinition.getRootAsSpecialDefinition(bb)),
  ],
  ["Api.Group", single("Group", (bb) => Group.getRootAsGroup(bb))],
  ["Api.Audit", single("Audit", (bb) => Audit.getRootAsAudit(bb))],

  // Report response types
  [
    "Reporting.Faults.FaultLogResponses",
    report("FaultLogResponses", (bb) => FaultLogResponses.getRootAsFaultLogResponses(bb)),
  ],
  [
    "Reporting.Traffic.TrafficWaitTimeListResponses",
    report("TrafficWaitTimeListResponses", (bb) =>
      TrafficWaitTimeListResponses.getRootAsTrafficWaitTimeListResponses(bb),
    ),
  ],
  [
    "Reporting.Traffic.TrafficSummaryFloorResponses",
    report("TrafficSummaryFloorResponses", (bb) =>
      TrafficSummaryFloorResponses.getRootAsTrafficSummaryFloorResponses(bb),
    ),
  ],
  [
    "Reporting.Traffic.TrafficSummaryDayResponses",
    report("TrafficSummaryDayResponses", (bb) =>
      TrafficSummaryDayResponses.getRootAsTrafficSummaryDayResponses(bb),
    ),
  ],
  [
    "Reporting.UsageReport.UsageDayCycleResponses",
    report("UsageDayCycleResponses", (bb) =>
      UsageDayCycleResponses.getRootAsUsageDayCycleResponses(bb),
    ),
  ],
  [
    "Reporting.UsageReport.UsageComparisonResponses",
    report("UsageComparisonResponses", (bb) =>
      UsageComparisonResponses.getRootAsUsageComparisonResponses(bb),
    ),
  ],
  [
    "Reporting.UsageReport.UsageDoorTimesResponses",
    report("UsageDoorTimesResponses", (bb) =>
      UsageDoorTimesResponses.getRootAsUsageDoorTimesResponses(bb),
    ),
  ],
  [
    "Reporting.Service.FlattenedServiceLogResponses",
    report("FlattenedServiceLogResponses", (bb) =>
      FlattenedServiceLogResponses.getRootAsFlattenedServiceLogResponses(bb),
    ),
  ],
  [
    "Reporting.Service.ServiceLogResponses",
    report("ServiceLogResponses", (bb) => ServiceLogResponses.getRootAsServiceLogResponses(bb)),
  ],
  [
    "Reporting.Frequency.FrequencyByLiftResponses",
    report("FrequencyByLiftResponses", (bb) =>
      FrequencyByLiftResponses.getRootAsFrequencyByLiftResponses(bb),
    ),
  ],
  [
    "Reporting.Frequency.FrequencyByFaultResponses",
    report("FrequencyByFaultResponses", (bb) =>
      FrequencyByFaultResponses.getRootAsFrequencyByFaultResponses(bb),
    ),
  ],
  [
    "Reporting.AllBanks.AllBanksBeneficialResponses",
    report("AllBanksBeneficialResponses", (bb) =>
      AllBanksBeneficialResponses.getRootAsAllBanksBeneficialResponses(bb),
    ),
  ],
  [
    "Reporting.AllBanks.AllBanksOutOfServiceResponses",
    report("AllBanksOutOfServiceResponses", (bb) =>
      AllBanksOutOfServiceResponses.getRootAsAllBanksOutOfServiceResponses(bb),
    ),
  ],
  [
    "Reporting.AllBanks.AllBanksPerformanceResponses",
    report("AllBanksPerformanceResponses", (bb) =>
      AllBanksPerformanceResponses.getRootAsAllBanksPerformanceResponses(bb),
    ),
  ],
  [
    "Reporting.AllBanks.AllBanksInterruptionResponses",
    report("AllBanksInterruptionResponses", (bb) =>
      AllBanksInterruptionResponses.getRootAsAllBanksInterruptionResponses(bb),
    ),
  ],
  [
    "Reporting.AllBanks.AllBanksEscalatorsUseResponses",
    report("AllBanksEscalatorsUseResponses", (bb) =>
      AllBanksEscalatorsUseResponses.getRootAsAllBanksEscalatorsUseResponses(bb),
    ),
  ],
  [
    "Reporting.AllBanks.AllBanksFireServiceResponses",
    report("AllBanksFireServiceResponses", (bb) =>
      AllBanksFireServiceResponses.getRootAsAllBanksFireServiceResponses(bb),
    ),
  ],

  // Mutation response (special: maps success boolean to status string)
  [
    "Api.MutationResponse",
    {
      name: "MutationResponse",
      verify: () => {},
      unpack: (payload: Uint8Array): unknown => {
        const bb = new flatbuffers.ByteBuffer(payload);
        const item = MutationResponse.getRootAsMutationResponse(bb).unpack();
        return {
          status: item.success ? "success" : "error",
          message: item.message ?? "",
        };
      },
    },
  ],

  // mTLS secrets listing for the per-channel mTLS wizard
  [
    "Api.MtlsSecretsResponse",
    {
      name: "MtlsSecretsResponse",
      verify: (payload: Uint8Array) => {
        const bb = new flatbuffers.ByteBuffer(payload);
        // Parse root and touch required vector length as a lightweight validity check.
        MtlsSecretsResponse.getRootAsMtlsSecretsResponse(bb).secretsLength();
      },
      unpack: (payload: Uint8Array): unknown => {
        const bb = new flatbuffers.ByteBuffer(payload);
        const root = MtlsSecretsResponse.getRootAsMtlsSecretsResponse(bb);
        const secrets = root.secretsLength();
        const result: { name: string; arn: string }[] = [];
        for (let i = 0; i < secrets; i += 1) {
          const ref = root.secrets(i);
          result.push({ name: ref!.name(), arn: ref!.arn() });
        }
        return { secrets: result };
      },
    },
  ],

  // Development-only Simulation API (LiftCloud /simulation/*)
  [
    "Api.SimulationAssetList",
    single("SimulationAssetList", (bb) => SimulationAssetList.getRootAsSimulationAssetList(bb)),
  ],
  [
    "Api.SimulationAsset",
    single("SimulationAsset", (bb) => SimulationAsset.getRootAsSimulationAsset(bb)),
  ],
  [
    "Api.SimulationScenarioList",
    single("SimulationScenarioList", (bb) =>
      SimulationScenarioList.getRootAsSimulationScenarioList(bb),
    ),
  ],
  [
    "Api.SimulationScenario",
    single("SimulationScenario", (bb) => SimulationScenario.getRootAsSimulationScenario(bb)),
  ],
  [
    "Api.SimulationTargetList",
    single("SimulationTargetList", (bb) => SimulationTargetList.getRootAsSimulationTargetList(bb)),
  ],
  [
    "Api.SimulationSandboxList",
    single("SimulationSandboxList", (bb) =>
      SimulationSandboxList.getRootAsSimulationSandboxList(bb),
    ),
  ],
  [
    "Api.SimulationRunList",
    single("SimulationRunList", (bb) => SimulationRunList.getRootAsSimulationRunList(bb)),
  ],
  ["Api.SimulationRun", single("SimulationRun", (bb) => SimulationRun.getRootAsSimulationRun(bb))],
];

/**
 * Resolves all queued registrations by computing SHA-256 hashes and
 * populating the schema registry. Called once at application startup.
 */
export async function initializeSchemaRegistry(): Promise<void> {
  const hashPromises = registrationQueue.map(async ([typeName, handler]) => {
    const encoder = new TextEncoder();
    const hash = await computeSchemaHash(encoder.encode(typeName));
    registerSchema(hash, { ...handler, typeName });
  });
  await Promise.all(hashPromises);
}

export { unpackPaginated, unpackSingle, unpackReport };
